import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import React from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  getOpsReport,
  type MartStatus,
  type OpReason,
  type OpRow,
  type OpsReport,
  type OpTotal,
  type PostingSync,
  type StandingSummary,
} from "@/api/warehouse_ops";
import { useAuth } from "@/auth/useAuth";
import { DateRangePicker } from "@/components/DateRangePicker";
import { Button } from "@/components/ui/button";
import { fmtDate, ruNum } from "@/lib/format";
import { usePortalConfig } from "@/portal/usePortalConfig";
import { LoadState } from "@/lib/scan-ui/screen";
import { cn } from "@/lib/utils";
import {
  buildDayPivot,
  buildTotalsMatrix,
  canOpenTariffs,
  cellFlagged,
  cellHint,
  type CellView,
  defaultOpsPeriod,
  type DirectionSum,
  martStatusText,
  partialReasons,
  type PivotLine,
  pivotAmountText,
  postingSyncText,
  reasonsText,
  sumView,
  todayMsk,
  totalView,
} from "@/lib/warehouse-ops";

import { BypassTab } from "./BypassTab";
import { DwhExportLine } from "./DwhExportLine";
import { ExcludedBlock } from "./ExcludedBlock";
import { type OpsDrill, OpsDetailSheet } from "./OpsDetailSheet";
import { PalletsSheet } from "./PalletsSheet";
import { RefetchError } from "./RefetchError";

type Drillable = Pick<OpRow, "direction" | "direction_title" | "operation" | "operation_title">;

/**
 * «Операции и стоимость»: операции склада по дням и направлениям × тариф,
 * действующий на день. Из портала — сборка паллет (моно/микс), отгрузка паллет,
 * ФБС Москва; из витрин DWH — хранение (паллеты по норме + собранные фактом,
 * ФБС коробами) и приёмка. Незаданный тариф виден словами, итог — с объёмом без
 * суммы рядом; витрины недоступны — ошибка на экране, а не нули.
 *
 * Итог — матрицей «статья × направление», «По дням» — сводной «операция × день»;
 * справочник тарифов — в настройках (`/settings/warehouse-billing`).
 */
export function WarehouseOpsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === TAB_BYPASS ? TAB_BYPASS : TAB_OPS;
  const select = (next: string) =>
    setParams(next === TAB_OPS ? {} : { tab: next }, { replace: true });
  return (
    <div className="mx-auto max-w-[1600px] space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Операции и стоимость</h1>
        <div role="tablist" aria-label="Разделы" className="mt-2 flex flex-wrap gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => select(t.key)}
              className={cn(
                "rounded-md border px-3 py-1 text-sm",
                tab === t.key ? "border-primary bg-primary text-primary-foreground" : "bg-white",
              )}
            >
              {t.title}
            </button>
          ))}
        </div>
      </div>
      {tab === TAB_BYPASS ? <BypassTab /> : <OpsTab />}
    </div>
  );
}

const TAB_OPS = "ops";
const TAB_BYPASS = "bypass";
const TABS = [
  { key: TAB_OPS, title: "Операции и стоимость" },
  { key: TAB_BYPASS, title: "Отгрузки мимо портала" },
];

function OpsTab(): React.ReactElement {
  const { user } = useAuth();
  const { hasPage, hasSection } = usePortalConfig();
  const [initFrom, initTo] = defaultOpsPeriod(todayMsk());
  // Интервал выбирается одним окном: `DateRangePicker` сам упорядочивает концы,
  // «конец раньше начала» здесь не бывает.
  const [from, setFrom] = React.useState(initFrom);
  const [to, setTo] = React.useState(initTo);
  const [drill, setDrill] = React.useState<OpsDrill | null>(null);
  const [palletsOpen, setPalletsOpen] = React.useState(false);

  const q = useQuery({
    queryKey: ["warehouse-ops", from, to],
    queryFn: () => getOpsReport(from, to),
  });

  const openDrill = (row: Drillable, dateFrom: string, dateTo: string) =>
    setDrill({
      dateFrom,
      dateTo,
      direction: row.direction,
      directionTitle: row.direction_title,
      operation: row.operation,
      operationTitle: row.operation_title,
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">
            Работа склада, хранение и приёмка по дням и направлениям, по тарифам с датой
          </p>
          {canOpenTariffs(user, hasPage, hasSection) && (
            <Link
              to="/settings/warehouse-billing"
              className="text-xs text-primary underline-offset-2 hover:underline"
            >
              Тарифы и нормы — в настройках
            </Link>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="w-60">
            <DateRangePicker
              from={from}
              to={to}
              ariaLabel="Период"
              onChange={(f, t) => {
                setFrom(f);
                setTo(t);
              }}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void q.refetch()}
            aria-label="Обновить"
          >
            <RefreshCw className="size-4" />
          </Button>
        </div>
      </div>

      {!q.data && (
        <LoadState isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()} />
      )}

      {q.data && q.error && <RefetchError error={q.error} onRetry={() => void q.refetch()} />}

      {q.data && (
        <>
          {q.data.notes.length > 0 && (
            <details className="rounded-lg border border-sky-200 bg-sky-50 text-xs text-sky-900">
              <summary className="cursor-pointer select-none px-3 py-2 font-medium">
                Как считается · {q.data.notes.length}
              </summary>
              <ul className="space-y-1 px-3 pb-3">
                {q.data.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </details>
          )}

          <MartBlock mart={q.data.mart} today={todayMsk()} />

          <DwhExportLine dwh={q.data.dwh} periodFrom={q.data.date_from} periodTo={q.data.date_to} />

          {q.data.standing && (
            <StandingBlock
              standing={q.data.standing}
              sync={q.data.posting_sync}
              onOpen={() => setPalletsOpen(true)}
            />
          )}

          {q.data.excluded && <ExcludedBlock excluded={q.data.excluded} />}

          <Totals
            report={q.data}
            onDrill={(t) => openDrill(t, q.data.date_from, q.data.date_to)}
          />

          <DayPivotSection report={q.data} onDrill={openDrill} />
        </>
      )}

      {drill && <OpsDetailSheet drill={drill} onClose={() => setDrill(null)} />}
      {palletsOpen && q.data && (
        <PalletsSheet
          dateFrom={q.data.date_from}
          dateTo={q.data.date_to}
          onClose={() => setPalletsOpen(false)}
        />
      )}
    </div>
  );
}

/** Витрины DWH: ошибка — красным и словами (хранения и приёмки нет), иначе —
 *  по какую дату остаток и за какие дни периода снимка нет (по загруженным датам,
 *  а не по строкам склада: пустой склад — ноль, не пропуск). */
function MartBlock({ mart, today }: { mart: MartStatus; today: string }): React.ReactElement {
  if (!mart.ok) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800"
      >
        <div className="font-semibold">Хранение и приёмка не посчитаны</div>
        <div>{mart.error}</div>
      </div>
    );
  }
  return (
    <div className="space-y-1 rounded-lg border bg-white p-3 text-xs text-muted-foreground">
      <div>{martStatusText(mart)}</div>
      {mart.missing_days.some((d) => d < today) && (
        <div className="text-amber-800">
          Нет снимка остатка в витрине:{" "}
          {mart.missing_days
            .filter((d) => d < today)
            .map(fmtDate)
            .join(", ")}{" "}
          (хранение за эти дни не посчитано)
        </div>
      )}
      {mart.missing_days.includes(today) && (
        <div className="text-amber-800">
          За сегодня данных ещё нет, сборка DWH ~10:30 — хранение за сегодня не посчитано
        </div>
      )}
    </div>
  );
}

function StandingBlock({
  standing,
  sync,
  onOpen,
}: {
  standing: StandingSummary;
  sync: PostingSync | null;
  onOpen: () => void;
}): React.ReactElement {
  return (
    <section
      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-white p-3 text-sm"
      aria-label="Собранные паллеты"
    >
      <div className="space-y-0.5">
        <div className="font-semibold">Собранные паллеты</div>
        <div>
          Стояли фактом: {ruNum.format(standing.standing)}
          {standing.standing_by_posting > 0 &&
            ` (из них отгружены мимо портала — по проведению в 1С: ${ruNum.format(standing.standing_by_posting)})`}
          {standing.standing_by_portal_order > 0 &&
            ` (через портал без скана паллеты — заказ отгружен сменой: ${ruNum.format(standing.standing_by_portal_order)})`}
        </div>
        {standing.doc_before_assembly > 0 && (
          <div className="text-amber-800">
            Отгружены в день сборки — дата документа в 1С раньше сборки (провели с плановой датой):{" "}
            {ruNum.format(standing.doc_before_assembly)} — не стояли ни дня
          </div>
        )}
        <div className={standing.no_signal > 0 ? "text-amber-800" : undefined}>
          Без сигнала отгрузки (ни скана в портале, ни проведённого документа в 1С):{" "}
          {ruNum.format(standing.no_signal)} (начали бы стоять в периоде) — фактом не считаются,
          товар по норме
        </div>
        {standing.not_applied > 0 && (
          <div className="text-amber-800">Не применены: {ruNum.format(standing.not_applied)}</div>
        )}
        {standing.shortfall_days > 0 && (
          <div className="text-amber-800">
            На паллетах больше, чем в остатке: {ruNum.format(standing.shortfall_qty)} шт. (
            {ruNum.format(standing.shortfall_days)} дн.-скл.)
          </div>
        )}
        {sync && (
          <div
            className={cn("text-xs", sync.ok ? "text-muted-foreground" : "text-rose-700")}
            role={sync.ok ? undefined : "status"}
          >
            {postingSyncText(sync)}
          </div>
        )}
      </div>
      <Button variant="outline" size="sm" onClick={onOpen}>
        Показать паллеты
      </Button>
    </section>
  );
}


/** Янтарная точка «есть что прочитать в подсказке»: причины, недостача, норма. */
function Dot(): React.ReactElement {
  return (
    <span
      aria-hidden
      className="ml-1 inline-block size-1.5 shrink-0 rounded-full bg-amber-500 align-middle"
    />
  );
}

/** Подсказка ячейки итога: сумма/объём словами плюс причины «не определено». */
function withReasons(hint: string, reasons: OpReason[]): string {
  return reasons.length > 0 ? `${hint}\n${reasonsText(reasons)}` : hint;
}

/** Ячейка итога: сумма крупно, объём мелко; без суммы — янтарём и словами. */
function ViewBlock({ view, dot }: { view: CellView; dot?: boolean }): React.ReactElement {
  return (
    <>
      <div
        className={cn(
          "whitespace-nowrap text-sm font-medium",
          view.warn && "text-amber-700",
        )}
      >
        {view.main}
        {dot && <Dot />}
      </div>
      {view.sub && (
        <div className={cn("text-xs", view.warn ? "text-amber-700" : "text-muted-foreground")}>
          {view.sub}
        </div>
      )}
    </>
  );
}

const STICKY = "sticky left-0 z-10";

/** «Итого за период» матрицей: строки — статьи (операция во всех направлениях),
 *  столбцы — направления в порядке сервера, «Итого» строки и столбца.
 *  Клик по ячейке — провал за период по паре направление × операция. */
function Totals({
  report,
  onDrill,
}: {
  report: OpsReport;
  onDrill: (t: OpTotal) => void;
}): React.ReactElement {
  const partial = partialReasons(report, todayMsk());
  const m = React.useMemo(() => buildTotalsMatrix(report.totals, report.rows), [report.totals, report.rows]);
  return (
    <section className="rounded-lg border bg-white p-3" aria-label="Итого за период">
      <h2 className="mb-2 text-base font-semibold">Итого за период</h2>
      {partial.length > 0 && (
        <div className="mb-2 text-sm text-rose-700" aria-label="Итог неполный">
          <p className="font-medium">Итог неполный:</p>
          <ul className="list-disc pl-5">
            {partial.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}
      {m.problems.length > 0 && (
        <div className="mb-2 text-sm text-rose-700" role="alert" aria-label="Итог не построен">
          <p className="font-medium">Итог не построен:</p>
          <ul className="list-disc pl-5">
            {m.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      {m.problems.length > 0 ? null : m.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">За период операций нет.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0 text-left text-sm tabular-nums">
            <thead className="text-xs text-muted-foreground">
              <tr>
                <th className={cn(STICKY, "border-b bg-white py-1 pr-3 font-medium")}>Статья</th>
                {m.columns.map((c) => (
                  <th key={c.direction} className="border-b px-2 py-1 text-right font-medium">
                    {c.title}
                  </th>
                ))}
                <th className="border-b bg-slate-50 px-2 py-1 text-right font-medium">Итого</th>
              </tr>
            </thead>
            <tbody>
              {m.rows.map((r) => (
                <tr key={r.operation} className="align-top">
                  <th
                    scope="row"
                    className={cn(
                      STICKY,
                      "min-w-[9rem] max-w-[16rem] border-b bg-white py-1.5 pr-3 font-normal",
                    )}
                  >
                    {r.title}
                  </th>
                  {r.cells.map((c, i) => (
                    <td key={m.columns[i]!.direction} className="border-b px-1 py-1 text-right">
                      {c ? (
                        <button
                          type="button"
                          className="w-full rounded px-1 py-0.5 text-right hover:bg-sky-50"
                          title={withReasons(totalView(c).hint, c.reasons)}
                          onClick={() => onDrill(c)}
                        >
                          <ViewBlock view={totalView(c)} dot={c.reasons.length > 0} />
                        </button>
                      ) : (
                        <span className="px-1 text-muted-foreground">—</span>
                      )}
                    </td>
                  ))}
                  <td
                    className="border-b bg-slate-50 px-2 py-1.5 text-right"
                    title={totalView(r.total).hint}
                  >
                    <ViewBlock view={totalView(r.total)} />
                  </td>
                </tr>
              ))}
              <tr className="align-top">
                <th scope="row" className={cn(STICKY, "bg-white py-1.5 pr-3 font-semibold")}>
                  Итого
                </th>
                {m.columnTotals.map((s) => (
                  <td key={s.direction} className="px-2 py-1.5 text-right font-semibold">
                    <ViewBlock view={sumView(s)} />
                  </td>
                ))}
                <td className="bg-slate-50 px-2 py-1.5 text-right font-semibold">
                  <ViewBlock view={sumView(m.grand)} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type PivotMode = "volume" | "amount";

/** «По дням» сводной: строки — направление (подзаголовок) → операция, столбцы —
 *  КАЖДЫЙ день периода (пустые дни не выкидываются), последний — «Итого».
 *  «Объём / Сумма» переключает содержимое ячеек; без тарифа сумма — словами. */
function DayPivotSection({
  report,
  onDrill,
}: {
  report: OpsReport;
  onDrill: (row: Drillable, dateFrom: string, dateTo: string) => void;
}): React.ReactElement {
  const [mode, setMode] = React.useState<PivotMode>("volume");
  const pivot = React.useMemo(
    () => buildDayPivot(report.rows, report.totals, report.date_from, report.date_to),
    [report],
  );
  const span = pivot.days.length + 2;
  return (
    <section className="rounded-lg border bg-white p-3" aria-label="По дням">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">По дням</h2>
        {report.rows.length > 0 && (
          <div className="inline-flex rounded-md border p-0.5 text-xs" role="group" aria-label="Показывать">
            {(
              [
                ["volume", "Объём"],
                ["amount", "Сумма"],
              ] as const
            ).map(([m, label]) => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
                className={cn(
                  "rounded px-3 py-1",
                  mode === m ? "bg-primary text-primary-foreground" : "hover:bg-slate-100",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
      {pivot.problems.length > 0 && (
        <div role="alert" className="mb-2 rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800">
          <p className="font-medium">Ответ сервера не сходится — сообщите разработчику:</p>
          <ul className="list-disc pl-5">
            {pivot.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      {report.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">За период операций нет.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="border-separate border-spacing-0 text-left text-xs tabular-nums">
            <thead className="text-muted-foreground">
              <tr>
                <th className={cn(STICKY, "min-w-[8rem] border-b bg-white py-1 pr-2 font-medium sm:min-w-[13rem]")}>
                  Операция
                </th>
                {pivot.days.map((d) => (
                  <th
                    key={d.iso}
                    className={cn(
                      "min-w-[3.25rem] border-b px-1 py-1 text-center font-medium",
                      d.weekend && "bg-slate-50 text-slate-400",
                    )}
                  >
                    <div>{d.label}</div>
                    <div className="text-[10px] font-normal">{d.weekday}</div>
                  </th>
                ))}
                <th className="min-w-[4.5rem] border-b bg-slate-50 px-1.5 py-1 text-right font-medium">
                  Итого
                </th>
              </tr>
            </thead>
            <tbody>
              {pivot.groups.map((g) => (
                <React.Fragment key={g.direction}>
                  <tr className="bg-slate-100">
                    <th scope="rowgroup" className={cn(STICKY, "bg-slate-100 py-1 pr-2 text-sm font-semibold")}>
                      {g.title}
                    </th>
                    {mode === "amount" ? (
                      <>
                        {g.daySums.map((s, i) => (
                          <td key={pivot.days[i]!.iso} className="px-1 py-1 text-right font-medium">
                            {s && <SumCell sum={s} />}
                          </td>
                        ))}
                        <td className="px-1.5 py-1 text-right font-semibold">
                          <SumCell sum={g.total} />
                        </td>
                      </>
                    ) : (
                      <td colSpan={span - 1} />
                    )}
                  </tr>
                  {g.lines.map((l) => (
                    <PivotLineRow
                      key={l.operation}
                      line={l}
                      days={pivot.days}
                      mode={mode}
                      onDay={(row) => onDrill(row, row.day, row.day)}
                      onTotal={() => onDrill(l, report.date_from, report.date_to)}
                    />
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Сумма направления за день/период в подзаголовке: без суммы — словами. */
function SumCell({ sum }: { sum: DirectionSum }): React.ReactElement {
  const v = sumView(sum);
  return (
    <span className={cn("whitespace-nowrap", v.warn && "text-amber-700")} title={v.hint}>
      {v.main}
      {v.warn && <Dot />}
    </span>
  );
}

function PivotLineRow({
  line,
  days,
  mode,
  onDay,
  onTotal,
}: {
  line: PivotLine;
  days: ReturnType<typeof buildDayPivot>["days"];
  mode: PivotMode;
  onDay: (row: OpRow) => void;
  onTotal: () => void;
}): React.ReactElement {
  const total = totalView(line.total);
  const totalWarn = mode === "amount" && total.warn;
  return (
    <tr className="align-top">
      <th scope="row" className={cn(STICKY, "border-b bg-white py-1 pl-2 pr-2 font-normal")}>
        <span className="text-foreground">{line.operation_title}</span>
        <span className="text-muted-foreground"> · {line.unit}</span>
      </th>
      {line.cells.map((c, i) => (
        <td
          key={days[i]!.iso}
          className={cn("border-b px-0.5 py-0.5 text-right", days[i]!.weekend && "bg-slate-50")}
        >
          {c && <DayCell row={c} mode={mode} onClick={() => onDay(c)} />}
        </td>
      ))}
      <td className="border-b bg-slate-50 px-0.5 py-0.5 text-right">
        <button
          type="button"
          onClick={onTotal}
          title={`За период: ${total.hint}`}
          className={cn(
            "w-full whitespace-nowrap rounded px-1 py-0.5 text-right font-medium hover:bg-sky-100",
            totalWarn && "text-amber-700",
          )}
        >
          {mode === "volume" ? ruNum.format(line.total.volume) : total.main}
          {totalWarn && <Dot />}
        </button>
      </td>
    </tr>
  );
}

function DayCell({
  row,
  mode,
  onClick,
}: {
  row: OpRow;
  mode: PivotMode;
  onClick: () => void;
}): React.ReactElement {
  // Вне категории ГП — «не считается» нейтрально: это не недобор.
  const unpriced = mode === "amount" && row.amount === null && !row.reference;
  return (
    <button
      type="button"
      onClick={onClick}
      title={cellHint(row)}
      className={cn(
        "w-full rounded px-1 py-0.5 text-right hover:bg-sky-100",
        unpriced
          ? "whitespace-normal text-[10px] font-medium leading-tight text-amber-700"
          : mode === "amount" && row.reference
            ? "whitespace-normal text-[10px] leading-tight text-muted-foreground"
            : "whitespace-nowrap",
      )}
    >
      {mode === "volume" ? ruNum.format(row.volume) : pivotAmountText(row)}
      {cellFlagged(row) && <Dot />}
    </button>
  );
}
